'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { createClientComponentClient } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from 'react-i18next'
import { useAdmin } from '@/hooks/useAdmin'
import {
    Heart, Plus, ArrowLeft,
    CheckCircle, AlertTriangle, Users,
    Send, UserPlus, Mic,
    Sparkles, UserCog, Mail, X,
    MessageSquare, Eye, EyeOff, Monitor
} from 'lucide-react'
import { ResidentMemoryForm } from '@/components/admin/ResidentMemoryForm'
import { MemberConversationViewer } from '@/components/admin/MemberConversationViewer'

// ─── Design tokens (matches Dashboard) ──────────────────────────────
const T = {
    sage: '#4a7c5e', sageDark: '#2d5a3d', sageDeep: '#1e3d2b',
    sageLight: '#e8f2ec', sageMid: '#b8d9c4',
    cream: '#faf7f2', creamDark: '#f0ebe0',
    gold: '#c9903a', navy: '#1a2740', navyMid: '#243452',
    white: '#ffffff', slate: '#4a5568', muted: '#8a94a6',
    pink: '#db2777', pinkLight: '#fce7f3', pinkMid: '#f472b6',
    fontDisplay: "'Playfair Display', Georgia, serif",
    fontBody: "'Source Serif 4', Georgia, serif",
}

interface FamilyHub {
    id: string; name: string; total_seats: number; active_seats: number
    pending_invites: number; voice_enabled: boolean; created_at: string
}

interface LovedOne {
    id: string; email: string; name: string | null
    created_at: string | null; last_voice_conversation_at: string | null
    preferences?: {
        allow_family_admin_view?: boolean
    } | null
}

export default function FamilyAdminPage() {
    const { user, isLoading: authLoading } = useAuth()
    const supabase = createClientComponentClient()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { t } = useTranslation()

    const [familyHub, setFamilyHub] = useState<FamilyHub | null>(null)
    const [lovedOnes, setLovedOnes] = useState<LovedOne[]>([])
    const [newEmail, setNewEmail] = useState('')
    const [newName, setNewName] = useState('')
    const [loading, setLoading] = useState(true)
    const [inviteLoading, setInviteLoading] = useState(false)
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | '' }>({ text: '', type: '' })
    const [showInviteForm, setShowInviteForm] = useState(false)
    const [selectedResident, setSelectedResident] = useState<{ id: string; name: string } | null>(null)
    const [viewingConversations, setViewingConversations] = useState<{ id: string; name: string } | null>(null)

    const tFamily = (key: string, fallback: string) => t(`family.admin.${key}`, fallback)

    const { isSuperAdmin } = useAdmin()

    const fetchFamilyData = useCallback(async () => {
        if (!user) return
        try {
            const urlHubId = searchParams.get('facility_id')
            const { data: profile } = await supabase.from('users').select('facility_id, facility_role').eq('id', user.id).single()

            // Allow if user is super admin OR admin/member of a hub
            if (isSuperAdmin || (profile?.facility_id && (profile?.facility_role === 'admin' || profile?.facility_role === 'member'))) {
                // Determine target hub: explicit param (superadmin only) or profile ID or default
                let targetHubId = profile?.facility_id || 'ae187aa9-9302-433b-80a0-17c3185e8f49'
                if (isSuperAdmin && urlHubId) {
                    targetHubId = urlHubId
                }

                const { data: hubData } = await supabase.from('facilities').select('*').eq('id', targetHubId).single()
                if (hubData) {
                    if (!isSuperAdmin) {
                        if (hubData.type === 'school' || hubData.type === 'family_edu') {
                            navigate('/education-admin', { replace: true })
                            return
                        } else if (hubData.type !== 'family') {
                            navigate('/facility-admin', { replace: true })
                            return
                        }
                    }
                    setFamilyHub(hubData as FamilyHub)
                }

                const residentFacilityId = targetHubId
                const { data: resData } = await supabase.from('users')
                    .select('id, email, name, created_at, last_voice_conversation_at, preferences')
                    .eq('facility_id', residentFacilityId)
                    .in('facility_role', ['resident', 'member'])
                    .order('created_at', { ascending: false })
                setLovedOnes((resData as LovedOne[]) || [])
            }
        } catch (err) { console.error('Error fetching family data:', err) }
        finally { setLoading(false) }
    }, [user?.id, supabase, navigate, isSuperAdmin, searchParams])

    useEffect(() => {
        if (!authLoading && user?.id) fetchFamilyData()
        else if (!authLoading && !user?.id) setLoading(false)
    }, [user?.id, authLoading, fetchFamilyData])

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault(); setInviteLoading(true); setMessage({ text: '', type: '' })
        try {
            const { data, error } = await supabase.functions.invoke('invite-resident', { body: { email: newEmail, name: newName } })
            if (error) throw error; if (data?.error) throw new Error(data.error)
            setMessage({ text: tFamily('inviteSuccess', 'Invitation sent successfully!'), type: 'success' }); setNewEmail(''); setNewName(''); setShowInviteForm(false); fetchFamilyData()
        } catch (err: unknown) { setMessage({ text: (err as Error).message || tFamily('inviteError', 'Failed to send invitation.'), type: 'error' }) }
        finally { setInviteLoading(false) }
    }

    const handleToggleVoice = async () => {
        if (!familyHub) return
        const newValue = !familyHub.voice_enabled
        setFamilyHub(prev => prev ? { ...prev, voice_enabled: newValue } : null)
        try { const { error } = await supabase.from('facilities').update({ voice_enabled: newValue }).eq('id', familyHub.id); if (error) throw error }
        catch { setFamilyHub(prev => prev ? { ...prev, voice_enabled: !newValue } : null) }
    }

    const handleToggleVisibility = async (memberId: string, currentStatus: boolean) => {
        const newStatus = !currentStatus
        // Optimistic update
        setLovedOnes(prev => prev.map(m => m.id === memberId ? {
            ...m,
            preferences: { ...m.preferences, allow_family_admin_view: newStatus }
        } : m))

        try {
            const { data: user } = await supabase.from('users').select('preferences').eq('id', memberId).single()
            const prefs = (user?.preferences as any) || {}
            const { error } = await supabase.from('users').update({
                preferences: { ...prefs, allow_family_admin_view: newStatus }
            }).eq('id', memberId)

            if (error) throw error
            setMessage({ text: `Member visibility ${newStatus ? 'enabled' : 'disabled'}.`, type: 'success' })
            setTimeout(() => setMessage({ text: '', type: '' }), 3000)
        } catch (err) {
            console.error('Error toggling visibility:', err)
            // Rollback
            setLovedOnes(prev => prev.map(m => m.id === memberId ? {
                ...m,
                preferences: { ...m.preferences, allow_family_admin_view: currentStatus }
            } : m))
            setMessage({ text: 'Failed to update visibility.', type: 'error' })
        }
    }

    if (authLoading || loading) {
        return (
            <div style={{ minHeight: '100vh', background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LoadingSpinner />
            </div>
        )
    }

    if (!user || (!familyHub && !isSuperAdmin)) {
        return (
            <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: T.fontBody }}>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    style={{ background: T.white, borderRadius: '24px', padding: '48px 40px', maxWidth: '480px', width: '100%', textAlign: 'center', border: `1px solid ${T.creamDark}`, boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
                    <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: T.pinkLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                        <Heart size={36} color={T.pink} />
                    </div>
                    <h2 style={{ fontFamily: T.fontDisplay, fontSize: '28px', fontWeight: 700, color: T.navy, marginBottom: '10px' }}>{tFamily('noAccess', 'No Family Hub Found')}</h2>
                    <p style={{ color: T.slate, marginBottom: '28px' }}>{tFamily('noAccessDesc', 'You do not have administrative access to a family hub.')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Button onClick={() => navigate('/dashboard')} variant="ghost" className="rounded-full h-12">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                        </Button>
                        <button onClick={() => navigate('/family-signup')} style={{ padding: '14px 28px', background: T.pink, color: T.white, border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody }}>
                            {tFamily('registerFamily', 'Create Family Hub')}
                        </button>
                    </div>
                </motion.div>
            </div>
        )
    }

    const seatsAvailable = (familyHub?.total_seats || 0) - ((familyHub?.active_seats || 0) + (familyHub?.pending_invites || 0))

    return (
        <div style={{ minHeight: '100vh', background: T.cream, fontFamily: T.fontBody }}>

            {/* ══ NAVY HERO BAND ══ */}
            <div style={{ background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 60%, ${T.sageDeep} 100%)`, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', border: '1px solid rgba(184,217,196,0.12)', pointerEvents: 'none' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '28px 20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <button onClick={() => navigate('/dashboard')} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.sageMid }}>
                                <ArrowLeft size={18} />
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ width: '44px', height: '44px', borderRadius: '14px', background: 'rgba(219,39,119,0.25)', border: '1px solid rgba(244,114,182,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Heart size={22} color={T.pinkMid} />
                                </div>
                                <div>
                                    <h1 style={{ fontFamily: T.fontDisplay, fontSize: '24px', fontWeight: 700, color: T.white, margin: 0, lineHeight: 1.2 }}>{familyHub?.name || 'Family Hub'}</h1>
                                    <p style={{ fontSize: '12px', fontWeight: 700, color: T.pinkMid, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>{tFamily('subtitle', 'Family Hub')}</p>
                                </div>
                            </div>
                        </div>
                        <button onClick={() => setShowInviteForm(true)} style={{ padding: '10px 22px', borderRadius: '20px', background: T.pink, color: T.white, border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 3px 12px rgba(219,39,119,0.35)' }}>
                            <Plus size={18} /> {tFamily('addLovedOne', 'Add Family Member')}
                        </button>
                    </div>
                </div>

                <svg viewBox="0 0 1440 48" style={{ display: 'block', width: '100%', height: '48px' }} preserveAspectRatio="none">
                    <path d="M0,48 C360,0 1080,48 1440,20 L1440,48 Z" fill={T.cream} />
                </svg>
            </div>

            {/* ══ CONTENT ══ */}
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 20px 80px' }}>

                {/* Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '16px', marginBottom: '36px' }}>
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                        style={{ background: T.white, borderRadius: '20px', padding: '24px', border: `1px solid ${T.creamDark}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(201,144,58,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gold }}><Users size={24} /></div>
                            <span style={{ fontSize: '10px', fontWeight: 800, color: T.gold, background: 'rgba(201,144,58,0.1)', padding: '4px 10px', borderRadius: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tFamily('hubCapacity', 'Hub Capacity')}</span>
                        </div>
                        <p style={{ fontSize: '36px', fontWeight: 900, color: T.navy, margin: 0, fontFamily: T.fontDisplay }}>{familyHub?.active_seats || 0} / {familyHub?.total_seats || 0}</p>
                        <p style={{ color: T.muted, fontWeight: 500, marginTop: '4px', fontSize: '14px' }}>{tFamily('profilesActive', 'Profiles Active')}</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
                        style={{ background: T.white, borderRadius: '20px', padding: '24px', border: `1px solid ${T.creamDark}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: familyHub?.voice_enabled ? '#3b5bdb' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: familyHub?.voice_enabled ? T.white : T.muted, transition: 'all 0.2s' }}><Mic size={24} /></div>
                            <button onClick={handleToggleVoice} style={{ padding: '6px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: 'none', background: familyHub?.voice_enabled ? '#3b5bdb' : T.creamDark, color: familyHub?.voice_enabled ? T.white : T.slate }}>
                                {familyHub?.voice_enabled ? 'Disable' : 'Enable'}
                            </button>
                        </div>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.navy, fontStyle: 'italic', margin: '0 0 4px' }}>{tFamily('voiceAccess', 'Voice Access')}:</h3>
                        <p style={{ fontSize: '24px', fontWeight: 900, color: familyHub?.voice_enabled ? '#3b5bdb' : T.muted, margin: 0 }}>
                            {familyHub?.voice_enabled ? tFamily('active', 'Active') : tFamily('inactive', 'Inactive')}
                        </p>
                    </motion.div>
                </div>

                {/* Loved Ones List */}
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ fontFamily: T.fontDisplay, fontSize: '24px', fontWeight: 700, color: T.navy, margin: '0 0 20px' }}>
                        {tFamily('lovedOnes', 'Loved Ones')}
                    </h2>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <AnimatePresence mode="popLayout">
                            {lovedOnes.length === 0 ? (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                    style={{ background: T.white, borderRadius: '20px', padding: '48px', textAlign: 'center', border: `1px solid ${T.creamDark}` }}>
                                    <div style={{ width: '56px', height: '56px', background: T.pinkLight, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                        <Heart size={28} color={T.pinkMid} />
                                    </div>
                                    <h3 style={{ fontWeight: 700, color: T.navy, margin: '0 0 8px', fontSize: '18px' }}>{tFamily('noProfiles', 'No profiles yet')}</h3>
                                    <p style={{ color: T.muted, margin: '0 0 20px' }}>{tFamily('inviteToBegin', 'Invite your loved one to generate their personal profile.')}</p>
                                    <button onClick={() => setShowInviteForm(true)} style={{ padding: '12px 28px', background: T.pink, color: T.white, border: 'none', borderRadius: '14px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: `0 4px 16px rgba(219,39,119,0.25)` }}>
                                        <Plus size={18} /> {tFamily('addLovedOne', 'Add Family Member')}
                                    </button>
                                </motion.div>
                            ) : lovedOnes.map((res) => (
                                <motion.div key={res.id} layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                                    style={{ background: T.white, borderRadius: '18px', padding: '24px', border: `1px solid ${T.creamDark}`, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(201,144,58,0.12)', border: `2px solid ${T.white}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gold, fontWeight: 900, fontSize: '22px' }}>
                                                    {res.name ? res.name.substring(0, 1) : '?'}
                                                </div>
                                                <div>
                                                    <h3 style={{ fontWeight: 700, color: T.navy, margin: 0, fontSize: '18px' }}>{res.name || 'Unnamed Profile'}</h3>
                                                    <p style={{ fontSize: '13px', color: T.muted, display: 'flex', alignItems: 'center', gap: '4px', margin: '2px 0 0' }}>
                                                        <Mail size={12} /> {res.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                    <button
                                                        onClick={() => handleToggleVisibility(res.id, res.preferences?.allow_family_admin_view !== false)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '6px 12px',
                                                            borderRadius: '12px',
                                                            background: res.preferences?.allow_family_admin_view !== false ? T.sageLight : '#fef2f2',
                                                            color: res.preferences?.allow_family_admin_view !== false ? T.sage : '#dc2626',
                                                            fontSize: '11px',
                                                            fontWeight: 800,
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        {res.preferences?.allow_family_admin_view !== false ? <Eye size={12} /> : <EyeOff size={12} />}
                                                        {res.preferences?.allow_family_admin_view !== false ? 'Shared' : 'Private'}
                                                    </button>
                                                </div>
                                                {res.last_voice_conversation_at ? (
                                                    <span style={{ fontSize: '10px', fontWeight: 800, color: T.sage, background: T.sageLight, padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase' }}>{tFamily('activeStatus', 'Active')}</span>
                                                ) : (
                                                    <span style={{ fontSize: '10px', fontWeight: 800, color: T.muted, background: T.creamDark, padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase' }}>{tFamily('pendingStatus', 'Pending')}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                                            <button onClick={() => setViewingConversations({ id: res.id, name: res.name || 'Family Member' })}
                                                style={{ flex: 1, padding: '10px', borderRadius: '12px', background: T.cream, color: T.navy, border: `1px solid ${T.creamDark}`, fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' }}
                                                className="hover:bg-white hover:shadow-sm"
                                            >
                                                <MessageSquare size={16} /> View Chats
                                            </button>
                                            <button onClick={() => setSelectedResident({ id: res.id, name: res.name || 'Loved One' })}
                                                style={{ flex: 1, padding: '10px', borderRadius: '12px', background: T.navy, color: T.white, border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <Sparkles size={16} /> {tFamily('personalize', 'Personalize')}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            {/* Invite Modal */}
            <AnimatePresence>
                {showInviteForm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-6"
                        style={{ background: 'rgba(26,39,64,0.5)', backdropFilter: 'blur(8px)' }}
                        onClick={() => setShowInviteForm(false)}>
                        <motion.div initial={{ scale: 0.95, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, y: 20, opacity: 0 }}
                            style={{ background: T.white, borderRadius: '24px', padding: '40px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', position: 'relative' }}
                            onClick={e => e.stopPropagation()}>
                            <button onClick={() => setShowInviteForm(false)} style={{ position: 'absolute', top: '20px', right: '20px', background: T.cream, border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.muted }}><X size={16} /></button>
                            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: T.pinkLight, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                                <UserPlus size={28} color={T.pink} />
                            </div>
                            <h2 style={{ fontFamily: T.fontDisplay, fontSize: '24px', fontWeight: 700, color: T.navy, margin: '0 0 8px' }}>{tFamily('addLovedOne', 'Add Family Member')}</h2>
                            <p style={{ color: T.muted, margin: '0 0 28px', fontSize: '15px' }}>{tFamily('addLovedOneDesc', 'Send them a secure login link. They will get their own private account tied to this hub.')}</p>
                            <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '6px' }}>Name</label>
                                    <Input placeholder="E.g., Dorothy" value={newName} required onChange={(e) => setNewName(e.target.value)} className="h-14 rounded-xl text-lg" style={{ background: T.cream, border: `1px solid ${T.creamDark}` }} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '11px', fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '6px' }}>{t('family.email_address')}</label>
                                    <Input type="email" required placeholder="dorothy@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="h-14 rounded-xl text-lg" style={{ background: T.cream, border: `1px solid ${T.creamDark}` }} />
                                </div>
                                <button type="submit" disabled={inviteLoading || seatsAvailable <= 0}
                                    style={{ padding: '14px', borderRadius: '14px', background: T.pink, color: T.white, border: 'none', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px', boxShadow: `0 4px 16px rgba(219,39,119,0.25)`, opacity: (inviteLoading || seatsAvailable <= 0) ? 0.5 : 1 }}>
                                    {inviteLoading ? <LoadingSpinner size="sm" /> : <><Send size={20} /> Send Invitation</>}
                                </button>
                                {seatsAvailable <= 0 && (
                                    <div style={{ background: 'rgba(201,144,58,0.1)', border: `1px solid rgba(201,144,58,0.3)`, padding: '14px', borderRadius: '12px', display: 'flex', gap: '10px', color: T.gold, fontSize: '14px' }}>
                                        <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                                        <p style={{ margin: 0 }}>{t('family.you_have_reached_the_maximum_number')}</p>
                                    </div>
                                )}
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Specialized Forms & Viewers */}
            <AnimatePresence>
                {selectedResident && (
                    <ResidentMemoryForm residentId={selectedResident.id} residentName={selectedResident.name} onClose={() => setSelectedResident(null)}
                        onSaved={() => { fetchFamilyData(); setMessage({ text: tFamily('personalizationUpdated', 'Personalization updated!'), type: 'success' }); setTimeout(() => setMessage({ text: '', type: '' }), 3000) }} />
                )}
                {viewingConversations && (
                    <MemberConversationViewer residentId={viewingConversations.id} residentName={viewingConversations.name} onClose={() => setViewingConversations(null)} />
                )}
            </AnimatePresence>

            {/* Notification Toast */}
            <AnimatePresence>
                {message.text && (
                    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[200]">
                        <div style={{ padding: '12px 24px', borderRadius: '50px', background: message.type === 'success' ? T.sage : '#dc2626', color: T.white, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 700, fontSize: '15px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
                            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                            {message.text}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
